import { Dialog, Transition } from '@headlessui/react'
import Translation from 'components/Translation'
import { Fragment, ReactElement, useEffect, useState } from 'react'

export interface DialogProps {
    show?: boolean
    prompt_message: string | ReactElement
    confirm_label?: string
    cancel_label?: string
    body?: string | JSX.Element
    onConfirm: (idx?: any) => void | void
    onCancel(): void
}
export default function ConfirmDialogModal({
    show,
    prompt_message = '',
    confirm_label = 'confirm',
    cancel_label = 'cancel',
    body,
    onConfirm = (): void => {},
    onCancel = (): void => {},
}: DialogProps) {
    const [is_open, setOpen] = useState<boolean>(false)
    const [message, setMessage] = useState<ReactElement | string>(
        prompt_message
    )

    function onConfirmModal() {
        setOpen(false)
        onConfirm()
    }

    function onCancelModal() {
        setOpen(false)
        onCancel()
    }

    useEffect(() => {
        if (!!prompt_message) {
            setMessage(prompt_message)
            setOpen(true)
        }
    }, [prompt_message])

    return (
        <Transition
            appear
            show={show != undefined ? show : is_open}
            as="div"
            enter="transition-opacity duration-75"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
        >
            <Dialog
                as="div"
                className="fixed inset-0 z-[101] overflow-y-auto"
                onClose={onCancelModal}
            >
                <div className="min-h-screen px-4 text-center">
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <Dialog.Overlay className="fixed inset-0 backdrop-filter backdrop-brightness-50" />
                    </Transition.Child>

                    {/* This element is to trick the browser into centering the modal contents. */}
                    <span
                        className="inline-block h-screen align-middle"
                        aria-hidden="true"
                    >
                        &#8203;
                    </span>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0 scale-95"
                        enterTo="opacity-100 scale-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100 scale-100"
                        leaveTo="opacity-0 scale-95"
                    >
                        <div className="inline-block text-center w-full max-w-md p-5 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
                            <div className="my-5">
                                <i className="feather-alert-triangle text-xl bg-red-500 text-white rounded-full p-2" />
                                <p className="text-primary text-lg font-medium font-display mt-3">
                                    {message}
                                </p>
                            </div>
                            {body}
                            <div className="flex justify-center gap-3 mt-5">
                                <button
                                    type="button"
                                    className="border border-gray-300 rounded-lg inline-block btn-gap"
                                    onClick={onCancelModal}
                                >
                                    <Translation
                                        content_key={cancel_label}
                                    />
                                </button>
                                <button
                                    type="button"
                                    className="bg-primary text-white rounded-lg btn-gap focus:outline-none hover:bg-primary-dark focus:ring-2 focus:ring-offset-2 focus:ring-primary-light"
                                    onClick={onConfirmModal}
                                >
                                    <Translation
                                        content_key={confirm_label}
                                    />
                                </button>
                            </div>
                        </div>
                    </Transition.Child>
                </div>
            </Dialog>
        </Transition>
    )
}
